import type { CSSProperties, ReactNode } from 'react';
import { ArrowRight, Loader2, TriangleAlert } from 'lucide-react';
import markUrl from '@/public/logo/mark-color.svg';
import '../chat/chat.css';
import { AuthPrimaryButton } from '../AuthControls';

export type VerifyStatus = 'verifying' | 'verified' | 'failed';

type SuccessCopy = { label: string; title: string; body: string };

type VerifiedTakeoverProps = {
  status: VerifyStatus;
  /** "Sign in": slides the panel away to reveal the form. */
  onContinue: () => void;
  /** The success wording. Defaults to the email confirmation; an accepted invitation passes its own. */
  copy?: SuccessCopy;
};

const VERIFIED_COPY: SuccessCopy = {
  label: 'Email confirmed',
  title: "You're verified.",
  body: 'Welcome to Pertento. Sign in to choose your plan and start your 12-day free trial.',
};

// The mark's own three colours plus a warm yellow, so the burst reads as the brand's
// rather than as generic confetti.
const COLORS = ['#24CBC7', '#3B60D9', '#7C5CFF', '#FBBF24'] as const;
const PIECE_COUNT = 32;

type Piece = { id: number; dx: number; dy: number; rot: number; delay: number; color: string; shape: number };

/**
 * Worked out once from the index, not with Math.random(): the pattern is the same on
 * every render and every visit, so it cannot flicker into a new arrangement when
 * something else re-renders the screen.
 */
const PIECES: readonly Piece[] = Array.from({ length: PIECE_COUNT }, (_, idx) => {
  const angle = (idx / PIECE_COUNT) * Math.PI * 2 + (idx % 3) * 0.12;
  const distance = 120 + ((idx * 53) % 110);

  return {
    id: idx,
    dx: Math.round(Math.cos(angle) * distance),
    // Flattened and lifted a little: the burst fans out and up before it falls.
    dy: Math.round(Math.sin(angle) * distance * 0.8) - 40,
    rot: ((idx * 97) % 360) + 180,
    delay: (idx % 6) * 40,
    color: COLORS[idx % COLORS.length] ?? COLORS[0],
    shape: idx % 3,
  };
});

const SHAPE_CLASS = ['h-2 w-2 rounded-[2px]', 'h-3 w-[5px] rounded-[2px]', 'h-[7px] w-[7px] rounded-full'] as const;

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0" aria-hidden="true">
    {PIECES.map((piece) => (
      <span
        key={piece.id}
        className={`pt-confetti absolute top-1/2 left-1/2 ${SHAPE_CLASS[piece.shape]}`}
        style={
          {
            backgroundColor: piece.color,
            animationDelay: `${300 + piece.delay}ms`,
            '--dx': `${piece.dx}px`,
            '--dy': `${piece.dy}px`,
            '--rot': `${piece.rot}deg`,
          } as CSSProperties
        }
      />
    ))}
  </div>
);

const step = (delay: number): CSSProperties => ({ animationDelay: `${delay}ms` });

/**
 * The badge. Two rings pulse out from it, the check draws itself, and the confetti
 * bursts once it has popped in.
 */
const VerifiedBadge = () => (
  <div className="relative flex h-20 w-20 items-center justify-center">
    <span className="pt-ring bg-accent absolute inset-0 rounded-full" aria-hidden="true" />
    <span className="pt-ring bg-accent absolute inset-0 rounded-full" style={step(600)} aria-hidden="true" />
    <span className="pt-pop bg-accent relative flex h-20 w-20 items-center justify-center rounded-full text-white">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          className="pt-draw"
          d="M5 12.5l4.5 4.5L19 7.5"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
    <Confetti />
  </div>
);

type StateProps = { badge: ReactNode; label?: string; title: string; body: string; cta?: ReactNode };

/** The shared stack: badge, label, headline, body, then the button. Staggered in. */
const State = ({ badge, label, title, body, cta }: StateProps) => (
  <div className="mx-auto flex w-full max-w-[560px] flex-1 flex-col items-center justify-center gap-5 text-center">
    {/* The margin keeps the label clear of the rings, which pulse out past the badge. */}
    <div className="mb-3">{badge}</div>

    {label && (
      <span
        className="pt-msg-in micro-label text-ok border-ok-line bg-ok-bg rounded-full border px-3 py-1.5"
        style={step(500)}
      >
        {label}
      </span>
    )}

    <h1
      className="pt-msg-in text-foreground m-0 text-[clamp(40px,5vw,64px)] leading-[1.02] font-medium tracking-[-0.04em] [text-wrap:balance]"
      style={step(600)}
    >
      {title}
    </h1>

    <p className="pt-msg-in text-dim m-0 max-w-[44ch] text-title leading-[1.6]" style={step(720)}>
      {body}
    </p>

    {cta && (
      <div className="pt-msg-in mt-3 w-full max-w-[360px]" style={step(860)}>
        {cta}
      </div>
    )}
  </div>
);

/**
 * The full screen panel for /auth/verify. It is the signup confirmation's sibling:
 * the same blue panel and centred stack, with the arrival a verified email deserves.
 * "Sign in" slides the panel back into its slot, which is the sign-in form being
 * revealed from underneath.
 */
export const VerifiedTakeover = ({ status, onContinue, copy = VERIFIED_COPY }: VerifiedTakeoverProps) => (
  <>
    <header className="flex justify-center">
      <img src={markUrl} alt="Pertento" className="h-[30px] w-auto" />
    </header>

    {/* Keyed on the status so the entrance plays again when the check resolves. */}
    {status === 'verifying' && (
      <State
        key="verifying"
        badge={
          <span className="bg-panel text-accent flex h-20 w-20 items-center justify-center rounded-full">
            <Loader2 size={28} strokeWidth={1.5} className="animate-spin motion-reduce:animate-none" />
          </span>
        }
        title="Confirming your email"
        body="One moment while we confirm your email address."
      />
    )}

    {status === 'verified' && (
      <State
        key="verified"
        badge={<VerifiedBadge />}
        label={copy.label}
        title={copy.title}
        body={copy.body}
        cta={
          <AuthPrimaryButton
            type="button"
            onClick={onContinue}
            autoFocus
            trailingIcon={<ArrowRight size={16} strokeWidth={1.5} />}
          >
            Sign in
          </AuthPrimaryButton>
        }
      />
    )}

    {status === 'failed' && (
      <State
        key="failed"
        badge={
          <span className="pt-pop bg-panel text-danger flex h-20 w-20 items-center justify-center rounded-full">
            <TriangleAlert size={28} strokeWidth={1.5} aria-hidden="true" />
          </span>
        }
        title="That link did not work"
        body="Check the link in your email, and get in touch with us if it still does not work."
        cta={
          <AuthPrimaryButton type="button" onClick={onContinue} autoFocus>
            Back to sign in
          </AuthPrimaryButton>
        }
      />
    )}
  </>
);
